/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jul 25 2024
 **/

import { useMemo } from "react";
import { useTheme } from "@emotion/react";
import { useQueryClient } from "@tanstack/react-query";

import { AccordionTemplate, Icon } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { _T } from "@rfice/language";

import * as S from "../emotion";
import SpaceDirectMessage from "./SpaceDirectMessage";

export default function SpaceChatItem({
	spaceList,
	item,
	currentChatSelected,
	handleSelectChat
}: {
	spaceList: WebServices.SpaceChat.List.Data;
	item: WebServices.SpaceChat.GroupedList.Data["groupedSpaceChats"][number];
	currentChatSelected: string;
	handleSelectChat: (channel: string) => void;
}) {
	const queryClient = useQueryClient();
	const theme = useTheme();

	const mySpaceListRes = queryClient.getQueryData<WebServices.Space.MySpaces.List.Response>(["space:myspace:list"]);

	const groupName = useMemo(() => {
		const externalGroups = mySpaceListRes?.data?.externalGroup || ([] as SwaggerModels.SpaceResponseVO[]);
		return externalGroups.some((exGroup) => exGroup.groupId === item.groupId) ? _T("외부그룹") : item.groupName;
	}, [mySpaceListRes, item]);

	return (
		<S.SpaceGroupContent>
			<S.GroupDivider />
			<AccordionTemplate
				container={{
					activeId: String(item.groupId)
				}}
				accordion={{
					id: String(item.groupId)
				}}
				header={{
					option: {
						style: {
							position: "sticky",
							top: 0,
							background: theme.colors.neutral_00_94,
							zIndex: 10
						}
					},
					render: ({ isAccordionOpen }) => (
						<S.SpaceGroupHeader>
							<Maybe test={isAccordionOpen} fallback={<Icon name="RightFilled16" size={16} />}>
								<Icon name="DownFilled16" size={16} />
							</Maybe>
							<S.GroupName typoSize="Body3_MD">{groupName}</S.GroupName>
						</S.SpaceGroupHeader>
					)
				}}
				body={{
					render: () => (
						<S.ShortDirectMessageList>
							{spaceList
								.filter((list) => list.groupId === item.groupId)
								.map((space) => {
									return (
										<li key={space.spaceId}>
											<SpaceDirectMessage
												spaceData={space}
												currentChatSelected={currentChatSelected}
												handleSelectChat={handleSelectChat}
											/>
										</li>
									);
								})}
						</S.ShortDirectMessageList>
					)
				}}
			/>
		</S.SpaceGroupContent>
	);
}
